import React from 'react';
import { useMarketData, useConnection, useTrading, useEngine } from '../context/MarketContext';
import { useMarketClock } from '../hooks/useMarketClock';
import { Navigation } from './Navigation';
import { Watchlist } from './Watchlist';
import OrderBookPanel from './OrderBookPanel';
import { resolveBySymbol } from '../constants/instruments';
import type { PageKey } from '../types';

// ── Info Tab sub-components ───────────────────────────────────────────────────

function fmtTZS(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toFixed(0);
}

function InventoryTab({ inventory, symbol }: { inventory: any; symbol: string }) {
  if (!inventory) {
    return <div style={{ padding: 20, textAlign: 'center', color: '#46525e', fontSize: 12 }}>No position for {symbol}</div>;
  }
  const skew: number = inventory.skew ?? 0;
  const util: number = inventory.utilization ?? 0;
  const skewClamped = Math.max(-1, Math.min(1, skew));
  const skewColor = Math.abs(skewClamped) > 0.7 ? '#ff5c5c' : Math.abs(skewClamped) > 0.35 ? '#ff8c3a' : '#36c98e';
  const utilColor = util > 0.8 ? '#ff5c5c' : util > 0.5 ? '#ff8c3a' : '#36c98e';
  const qty: number = inventory.quantity ?? 0;
  const netUnsettled: number = (inventory.unsettled_buy_qty ?? 0) - (inventory.unsettled_sell_qty ?? 0);

  return (
    <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#d7dde4' }}>{symbol}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: qty >= 0 ? '#36c98e' : '#ff5c5c' }}>
          {qty >= 0 ? '+' : ''}{qty.toLocaleString()}
        </span>
      </div>
      {inventory.avg_cost != null && inventory.avg_cost > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#7a8693' }}>
          <span>Avg Cost</span>
          <span style={{ color: '#7e8a96', fontVariantNumeric: 'tabular-nums' }}>
            {(inventory.avg_cost as number).toFixed(2)} <span style={{ color: '#46525e' }}>TZS</span>
          </span>
        </div>
      )}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#7a8693', marginBottom: 3 }}>
          <span>Skew</span>
          <span style={{ color: skewColor, fontWeight: 600 }}>{skewClamped >= 0 ? '+' : ''}{skewClamped.toFixed(2)}</span>
        </div>
        <div style={{ position: 'relative', height: 4, backgroundColor: '#2a3540', borderRadius: 2 }}>
          <div style={{ position: 'absolute', left: '50%', top: 0, width: 1, height: 4, backgroundColor: '#46525e' }} />
          <div style={{
            position: 'absolute', top: 0,
            left: skewClamped >= 0 ? '50%' : `${((skewClamped + 1) / 2) * 100}%`,
            width: `${Math.abs(skewClamped) * 50}%`,
            height: 4, backgroundColor: skewColor, borderRadius: 2,
          }} />
        </div>
      </div>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#7a8693', marginBottom: 3 }}>
          <span>Utilization</span>
          <span style={{ color: utilColor, fontWeight: 600 }}>{(util * 100).toFixed(0)}%</span>
        </div>
        <div style={{ height: 4, backgroundColor: '#2a3540', borderRadius: 2 }}>
          <div style={{ height: 4, width: `${Math.min(100, util * 100)}%`, backgroundColor: utilColor, borderRadius: 2, transition: 'width 0.3s' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, backgroundColor: inventory.can_buy ? 'rgba(54,201,142,0.1)' : 'rgba(255,92,92,0.1)', color: inventory.can_buy ? '#36c98e' : '#ff5c5c' }}>
          {inventory.can_buy ? '✓' : '✗'} BUY
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, backgroundColor: inventory.can_sell ? 'rgba(54,201,142,0.1)' : 'rgba(255,92,92,0.1)', color: inventory.can_sell ? '#36c98e' : '#ff5c5c' }}>
          {inventory.can_sell ? '✓' : '✗'} SELL
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
        {[
          { label: 'Settled', value: inventory.settled_quantity ?? 0 },
          { label: 'Pend. Buy', value: inventory.unsettled_buy_qty ?? 0, color: '#36c98e' },
          { label: 'Pend. Sell', value: inventory.unsettled_sell_qty ?? 0, color: '#ff5c5c' },
        ].map(item => (
          <div key={item.label} style={{ backgroundColor: '#0a0d10', borderRadius: 4, padding: '4px 6px' }}>
            <div style={{ fontSize: 9, color: '#46525e', marginBottom: 2 }}>{item.label}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: item.color || '#7e8a96' }}>{(item.value as number).toLocaleString()}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#7a8693' }}>
        <span>Net unsettled</span>
        <span style={{ color: netUnsettled >= 0 ? '#36c98e' : '#ff5c5c', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {netUnsettled >= 0 ? '+' : ''}{netUnsettled.toLocaleString()}
        </span>
      </div>
    </div>
  );
}

function SettlementTab({ report }: { report: any }) {
  if (!report) {
    return <div style={{ padding: 20, textAlign: 'center', color: '#46525e', fontSize: 12 }}>No settlement data</div>;
  }
  const cashCells = [
    { label: 'Available', value: report.available_cash },
    { label: 'Tradeable', value: report.tradeable_cash, color: '#36c98e' },
    { label: 'Committed', value: report.committed_cash, color: '#ff8c3a' },
    { label: 'Receivables', value: report.receivables, color: '#4da3ff' },
  ];
  const dates: any[] = report.settlement_dates ?? [];
  const totalNet: number = dates.reduce((sum: number, sd: any) => sum + (sd.net ?? sd.amount ?? 0), 0);
  return (
    <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {cashCells.map(cell => (
          <div key={cell.label} style={{ backgroundColor: '#0a0d10', borderRadius: 6, padding: '6px 8px' }}>
            <div style={{ fontSize: 9, color: '#46525e', marginBottom: 3 }}>{cell.label}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: cell.color || '#7e8a96' }}>
              {fmtTZS(cell.value ?? 0)} <span style={{ fontSize: 9, color: '#46525e', fontWeight: 400 }}>TZS</span>
            </div>
          </div>
        ))}
      </div>
      {dates.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#46525e', letterSpacing: '0.06em' }}>UPCOMING</div>
          {dates.slice(0, 4).map((sd: any, i: number) => {
            const rawDate: string = sd.date || sd.settlement_date || '';
            const parsed = rawDate ? new Date(rawDate) : null;
            const dateLabel = parsed && !isNaN(parsed.getTime())
              ? `T+${i + 1} (${(parsed.getMonth() + 1).toString().padStart(2, '0')}/${parsed.getDate().toString().padStart(2, '0')})`
              : rawDate || `T+${i + 1}`;
            const amt: number = sd.net ?? sd.amount ?? 0;
            return (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#7e8a96' }}>
                <span style={{ color: '#7a8693' }}>{dateLabel}</span>
                <span style={{ color: amt >= 0 ? '#36c98e' : '#ff5c5c', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {amt >= 0 ? '+' : ''}{fmtTZS(Math.abs(amt))} TZS
                </span>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, borderTop: '1px solid #2a3540', paddingTop: 4, marginTop: 2 }}>
            <span style={{ color: '#46525e', fontWeight: 700 }}>Net</span>
            <span style={{ color: totalNet >= 0 ? '#36c98e' : '#ff5c5c', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              {totalNet >= 0 ? '+' : ''}{fmtTZS(Math.abs(totalNet))} TZS
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function TradesTab({ trades }: { trades: any[] }) {
  if (!trades || trades.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: '#46525e', fontSize: 12 }}>No recent trades</div>;
  }
  const timeShort = (t: string) => {
    const d = new Date(t);
    if (!Number.isFinite(d.getTime())) return t;
    return d.toLocaleTimeString('en-TZ', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: 'Africa/Nairobi' });
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '70px 44px 1fr 64px 58px', gap: 8, padding: '6px 12px', borderBottom: '1px solid #2a3540', fontSize: 9, fontWeight: 700, color: '#46525e', letterSpacing: '0.06em' }}>
        <div>TIME</div><div>SIDE</div><div>PRICE</div><div style={{ textAlign: 'right' }}>QTY</div><div style={{ textAlign: 'right' }}>P&L</div>
      </div>
      {trades.slice(0, 20).map((t: any, i: number) => {
        const isBuy = t.side?.toLowerCase() === 'buy';
        const pnl: number = t.realized_pnl ?? 0;
        return (
          <div key={i} style={{ padding: '6px 12px', borderBottom: i < trades.length - 1 ? '1px solid #0a0d10' : 'none' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '70px 44px 1fr 64px 58px', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 10, color: '#7a8693', fontVariantNumeric: 'tabular-nums' }}>{timeShort(t.time)}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: isBuy ? '#36c98e' : '#ff5c5c' }}>{isBuy ? 'BUY' : 'SELL'}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#d7dde4', fontVariantNumeric: 'tabular-nums' }}>{(t.price ?? 0).toFixed(2)}</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: t.is_maker ? '#4da3ff' : '#46525e' }}>{t.is_maker ? 'M' : 'T'}</span>
              </div>
              <span style={{ textAlign: 'right', fontSize: 10, color: '#7e8a96', fontVariantNumeric: 'tabular-nums' }}>{(t.size ?? 0).toLocaleString()}</span>
              <span style={{ textAlign: 'right', fontSize: 10, fontWeight: 600, color: pnl > 0 ? '#36c98e' : pnl < 0 ? '#ff5c5c' : '#46525e', fontVariantNumeric: 'tabular-nums' }}>
                {pnl > 0 ? '+' : pnl < 0 ? '' : ''}{pnl !== 0 ? fmtTZS(pnl) : '—'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── AppLayout ─────────────────────────────────────────────────────────────────

export interface AppLayoutProps {
  activePage: PageKey;
  setActivePage: (p: PageKey) => void;
  instruments: { label: string; secId: string }[];
  addInstrument: (label: string, secId: string) => void;
  removeInstrument: (secId: string) => void;
  selectedBookPrice: number | undefined;
  setSelectedBookPrice: (p: number | undefined) => void;
  infoTab: 'inventory' | 'settlement' | 'trades';
  setInfoTab: (t: 'inventory' | 'settlement' | 'trades') => void;
  renderPage: () => React.ReactNode;
}

export function AppLayout({
  activePage, setActivePage,
  instruments, addInstrument, removeInstrument,
  selectedBookPrice, setSelectedBookPrice,
  infoTab, setInfoTab, renderPage,
}: AppLayoutProps) {
  const { selectedSymbol, setSelectedSymbol, INSTRUMENTS, orderBook, recentTrades, labelForSecId } = useMarketData();
  const { connectionStatus, messages } = useConnection();
  const { isAutoTrading } = useTrading();
  const { inventoryBySymbol, settlementReport } = useEngine();
  const market = useMarketClock();

  return (
    <>
      {/* Bottom nav rail. Rendered BEFORE the workspace in the DOM but painted
          at the bottom of the viewport (position: fixed), so keyboard Tab order
          reaches the nav before the page content -- the a11y answer to the usual
          "bottom nav is a tab-order trap" objection. */}
      <Navigation activePage={activePage} setActivePage={setActivePage} />

      {/* Main Content Grid */}
      <div className="main-content-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '12px', padding: '12px' }}>

        {/* Center Panel */}
        <div className="center-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {renderPage()}
        </div>

        {/* Right Panel. The Watchlist sits above the book: instrument selection
            belongs next to the book you are selecting into, and it was the only
            remaining occupant of the deleted left column. */}
        <div className="right-panel" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Watchlist
            selectedSymbol={selectedSymbol}
            setSelectedSymbol={setSelectedSymbol}
            instruments={instruments}
            allInstruments={INSTRUMENTS}
            onAdd={addInstrument}
            onRemove={removeInstrument}
          />
          <OrderBookPanel onPriceSelect={setSelectedBookPrice} />

          {/* Info Tabs */}
          <div style={{ backgroundColor: '#10151b', borderRadius: 8, border: '1px solid #2a3540', overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', borderBottom: '1px solid #2a3540' }}>
              {(['inventory', 'settlement', 'trades'] as const).map(tab => (
                <div
                  key={tab}
                  onClick={() => setInfoTab(tab)}
                  style={{
                    padding: '8px 0', textAlign: 'center', cursor: 'pointer',
                    fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                    color: infoTab === tab ? '#93c5fd' : '#46525e',
                    borderBottom: `2px solid ${infoTab === tab ? '#4da3ff' : 'transparent'}`,
                    transition: 'color 0.15s',
                  }}
                >
                  {tab === 'inventory' ? 'INVENTORY' : tab === 'settlement' ? 'SETTLEMENT' : 'TRADES'}
                </div>
              ))}
            </div>
            <div style={{ maxHeight: 220, overflowY: 'auto' }}>
              {infoTab === 'inventory' && <InventoryTab inventory={resolveBySymbol(inventoryBySymbol, selectedSymbol)} symbol={labelForSecId(selectedSymbol)} />}
              {infoTab === 'settlement' && <SettlementTab report={settlementReport} />}
              {infoTab === 'trades' && <TradesTab trades={recentTrades} />}
            </div>
          </div>
        </div>
      </div>

      {/* Pinned status bar */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#0a0d10', borderTop: '1px solid #10151b', padding: '0 20px', height: 28, display: 'flex', alignItems: 'center', gap: 20, zIndex: 200, flexShrink: 0 }}>
        {[
          { label: `DSE ${market.label}`, value: market.window, color: market.open ? '#36c98e' : '#ff5c5c' },
          { label: 'FIX', value: connectionStatus === 'connected' ? 'ACTIVE' : connectionStatus === 'connecting' ? 'CONNECTING' : 'INACTIVE', color: connectionStatus === 'connected' ? '#36c98e' : connectionStatus === 'connecting' ? '#ff8c3a' : '#ff5c5c' },
          { label: 'Market Data', value: (orderBook.bids.length || orderBook.asks.length) ? 'STREAMING' : 'NO DATA', color: (orderBook.bids.length || orderBook.asks.length) ? '#36c98e' : '#ff5c5c' },
          { label: 'Auto-Trading', value: isAutoTrading ? 'ON' : 'OFF', color: isAutoTrading ? '#36c98e' : '#7a8693' },
          { label: 'Messages', value: messages.length.toLocaleString(), color: '#4da3ff' },
          { label: 'Book Entries', value: (orderBook.bids.length + orderBook.asks.length).toLocaleString(), color: '#4da3ff' },
        ].map((item, i, arr) => (
          <React.Fragment key={item.label}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: item.color, flexShrink: 0 }} />
              <span style={{ fontSize: 10, color: '#46525e' }}>{item.label}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: item.color, letterSpacing: '0.04em' }}>{item.value}</span>
            </div>
            {i < arr.length - 1 && <div style={{ width: 1, height: 12, backgroundColor: '#10151b' }} />}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}
